import * as TooltipPrimitives from "@radix-ui/react-tooltip";
import { cva } from "class-variance-authority";
import React, { useEffect, useState } from "react";

import { cn } from "../../../lib/utils";

import { usePortalContainer } from "../../../contexts";

export const tooltipVariants = cva(
	"data-[side=bottom]:animate-slideDownAndFade data-[side=left]:animate-slideLeftAndFade data-[side=right]:animate-slideRightAndFade data-[side=top]:animate-slideUpAndFade data-[state=closed]:animate-hide z-[999999] max-w-64 select-none rounded-1 bg-neutral-8 text-neutral-0 shadow-md will-change-[transform,opacity]",
	{
		variants: {
			size: {
				medium: "p-2 text-caption",
				small: "p-1 text-extra-small",
			},
		},
		defaultVariants: {
			size: "medium",
		},
	}
);

/**
 * Tooltip component based off of radix UI's Tooltip component. https://www.radix-ui.com/primitives/docs/components/tooltip
 */
interface TooltipProps
	extends
		Omit<TooltipPrimitives.TooltipContentProps, "content" | "onClick">,
		Pick<TooltipPrimitives.TooltipProps, "open" | "defaultOpen" | "onOpenChange" | "delayDuration"> {
	/**
	 * The content of the tooltip.
	 */
	content: string | React.ReactNode;
	/**
	 * Size of the tooltip.
	 * @default "medium"
	 */
	size?: "small" | "medium";
	/**
	 * onClick event handler for the trigger element.
	 */
	onClick?: React.MouseEventHandler<HTMLButtonElement>;
	/**
	 * Placement of tooltip.
	 * @default "top"
	 */
	placement?: "bottom" | "left" | "top" | "right";
	/**
	 * does the tooltip have an arrow
	 */
	showArrow?: boolean;
	/**
	 * offset from the target element in pixels
	 * @default 8
	 */
	offset?: number;
	/**
	 * is the tooltip opened on click instead of on Hover
	 */
	openOnClick?: boolean;
	/**
	 * turn tooltip off after a certain amount of time in milliseconds
	 */
	turnOffAfter?: number | null;
	/**
	 * If false, the tooltip will be displayed
	 */
	disabled?: boolean;
	/**
	 * maximum width to use for the tooltip
	 */
	maxWidth?: number;
	/**
	 * className to apply to the tooltip content
	 */
	dangerouslySetClassName?: string;
	/**
	 * Custom portal container element to render the tooltip within.
	 * Falls back to PortalContext if available, then main-body-content, then document.body.
	 *
	 * Priority order:
	 * 1. Explicit portalContainer prop
	 * 2. PortalContext container (automatic detection via usePortalContainer)
	 * 3. main-body-content element
	 * 4. document.body
	 *
	 * When used inside components like PanelBase, the context automatically provides
	 * the correct container, eliminating the need to manually pass this prop.
	 */
	portalContainer?: HTMLElement | null;
	/**
	 * If true, the Tooltip will not wrap children in its own element.
	 * Instead, it will pass trigger props directly to the child element.
	 * Use this when the child is already a button or interactive element.
	 */
	asChild?: boolean;
}

const Tooltip = React.forwardRef<React.ElementRef<typeof TooltipPrimitives.Content>, TooltipProps>(
	(
		{
			children,
			dangerouslySetClassName,
			content,
			size = "medium",
			delayDuration,
			defaultOpen,
			open,
			onClick,
			onOpenChange,
			placement,
			showArrow = true,
			offset = 8,
			openOnClick = false,
			asChild,
			turnOffAfter,
			disabled = false,
			portalContainer,
			...props
		}: TooltipProps,
		forwardedRef
	) => {
		// state to manage tooltip open state if openOnClick is specified
		const [manualIsOpen, setManualIsOpen] = useState(false);

		// Get portal container from context as fallback
		const contextPortalInfo = usePortalContainer();

		// Determine the final portal container to use
		// Priority: explicit portalContainer prop > context container > main-body-content > document.body
		const finalPortalContainer =
			portalContainer ||
			contextPortalInfo?.container ||
			document.getElementById("main-body-content") ||
			document.body;

		// if turnOffAfter is set, close the tooltip after the specified time
		useEffect(() => {
			if (turnOffAfter && manualIsOpen) {
				setTimeout(() => {
					setManualIsOpen(false);
				}, turnOffAfter);
			}
		}, [manualIsOpen, turnOffAfter]);

		return (
			<TooltipPrimitives.Provider delayDuration={150}>
				<TooltipPrimitives.Root
					open={openOnClick ? manualIsOpen : open}
					defaultOpen={defaultOpen}
					onOpenChange={onOpenChange}
					delayDuration={delayDuration}
					tremor-id="tremor-raw"
				>
					<TooltipPrimitives.Trigger
						type="button"
						className={asChild ? undefined : "border-none bg-transparent p-0"}
						onClick={(e) => {
							onClick && onClick(e);
							if (openOnClick) {
								setManualIsOpen(!manualIsOpen);
							}
						}}
						asChild={asChild}
					>
						{children}
					</TooltipPrimitives.Trigger>
					{!disabled && (
						<TooltipPrimitives.Portal
							// Use the final portal container determined by priority chain
							container={finalPortalContainer}
						>
							<TooltipPrimitives.Content
								ref={forwardedRef}
								side={placement}
								sideOffset={offset}
								align="center"
								className={cn(
									tooltipVariants({
										size,
									}),
									dangerouslySetClassName
								)}
								{...props}
							>
								{content}
								{showArrow && (
									<TooltipPrimitives.Arrow
										className="border-none fill-neutral-8"
										width={6}
										height={4}
										aria-hidden="true"
									/>
								)}
							</TooltipPrimitives.Content>
						</TooltipPrimitives.Portal>
					)}
				</TooltipPrimitives.Root>
			</TooltipPrimitives.Provider>
		);
	}
);

Tooltip.displayName = "Tooltip";

export { Tooltip, type TooltipProps };
